import { useState, useEffect, useRef } from "react";
import SessionStore from "../../../model/SessionStore";
import MetricChart from "../../../components/MetricChart";
import { MetricsRequest, MetricResponse, DeviceData } from "../../../model/model";
import { Select, Modal, Button, Space, Card } from "antd";
import { SettingOutlined } from "@ant-design/icons";

interface GraphWidgetProps {
  config: { deviceName?: string };
  onConfigChange: (config: { deviceName?: string }) => void;
  startDate: Date;
  endDate: Date;
  aggregator: string;
  downsampling: string;
  refreshKey: number;
}

function GraphWidget(props: GraphWidgetProps) {
  const [deviceMetrics, setDeviceMetrics] = useState<MetricResponse | undefined>(undefined);
  const [devices, setDevices] = useState<DeviceData[]>([]);
  const [configModalVisible, setConfigModalVisible] = useState(false);
  const [selectedDeviceName, setSelectedDeviceName] = useState<string | undefined>(props.config.deviceName);
  const containerRef = useRef<HTMLDivElement>(null);
  const [size, setSize] = useState({width: 600, height: 300});

  useEffect(() => {
    const updateSize = () => {
      if (containerRef.current) {
        const rect = containerRef.current.getBoundingClientRect();
        setSize({width: rect.width, height: rect.height});
      }
    };
    updateSize();
    window.addEventListener('resize', updateSize);
    return () => window.removeEventListener('resize', updateSize);
  }, []);

  useEffect(() => {
    SessionStore.getClient().getAllDevices(SessionStore.getBearer()).then((resp: { data: DeviceData[] }) => {
      setDevices(resp.data);
    });
  }, []);

  useEffect(() => {
    if (!props.config.deviceName) {
      setDeviceMetrics(undefined);
      return;
    }

    const device = devices.find(d => d.name === props.config.deviceName);
    if (!device) {
      return;
    }

    const req: MetricsRequest = {
      topic: device.topic,
      start: props.startDate.getTime(),
      end: props.endDate.getTime(),
      cols: [],
      limit: 1000,
      downsampling: props.downsampling,
      aggregator: props.aggregator,
      desc: false,
    };

    SessionStore.getClient().getMetrics(req, SessionStore.getBearer()).then((resp: { data: MetricResponse }) => {
      setDeviceMetrics(resp.data);
    });
  }, [props.startDate, props.endDate, props.aggregator, props.downsampling, props.config.deviceName, devices, props.refreshKey]);

  const handleSaveConfig = () => {
    props.onConfigChange({ deviceName: selectedDeviceName });
    setConfigModalVisible(false);
  };

  if (deviceMetrics === undefined) {
    return (
      <div style={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column' }}>
        {!props.config.deviceName ? (
          <Button type="link" icon={<SettingOutlined />} onClick={() => setConfigModalVisible(true)}>
            Configure Device
          </Button>
        ) : (
          <div>Loading...</div>
        )}
        <Modal
          title="Graph Widget Configuration"
          open={configModalVisible}
          onOk={handleSaveConfig}
          onCancel={() => setConfigModalVisible(false)}
        >
          <Space direction="vertical" style={{ width: '100%' }}>
            <span>Device:</span>
            <Select
              style={{ width: '100%' }}
              value={selectedDeviceName}
              onChange={setSelectedDeviceName}
              placeholder="Select a device"
              options={devices.map((d: DeviceData) => ({ label: d.name, value: d.name }))}
            />
          </Space>
        </Modal>
      </div>
    );
  }

  return (
    <div ref={containerRef} style={{ height: '100%', width: '100%', position: 'relative' }}>
      <Button
        size="small"
        icon={<SettingOutlined />}
        style={{ position: 'absolute', top: 0, right: 24, zIndex: 10 }}
        onClick={() => {
          setSelectedDeviceName(props.config.deviceName);
          setConfigModalVisible(true);
        }}
      />
      <Card title={props.config.deviceName} className="dashboard-chart" style={{ height: '100%', display: 'flex', flexDirection: 'column' }} bodyStyle={{ flex: 1, minHeight: 0 }}>
        <MetricChart metric={deviceMetrics} aggregation={props.downsampling} zeroToNull width={size.width} height={size.height} />
      </Card>
      <Modal
        title="Graph Widget Configuration"
        open={configModalVisible}
        onOk={handleSaveConfig}
        onCancel={() => setConfigModalVisible(false)}
      >
        <Space direction="vertical" style={{ width: '100%' }}>
          <span>Device:</span>
          <Select
            style={{ width: '100%' }}
            value={selectedDeviceName}
            onChange={setSelectedDeviceName}
            placeholder="Select a device"
            options={devices.map(d => ({ label: d.name, value: d.name }))}
          />
        </Space>
      </Modal>
    </div>
  );
}

export default GraphWidget;
